import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AnalyzedText from './AnalyzedText';
import DiscourseMarkersPanel from './analyzer/DiscourseMarkersPanel';
import SemanticsPanel from './analyzer/SemanticsPanel';
import ClassifierPanel from './analyzer/ClassifierPanel';

/**
 * Contenidor de l'analitzador de text. Els quatre modes d'anàlisi
 * (morfològic, marcadors del discurs, semàntica i classificació) s'usen per
 * separat, no barrejats, així que es presenten com a pestanyes dins un sol
 * modal. Només es mostra el panell del mode actiu.
 *
 * De moment només el mode morfològic està operatiu (embolcalla l'`AnalyzedText`
 * existent, que ja rep les dades fetes des de CustomEditor). Els altres tres
 * modes són panells en construcció, preparats perquè cadascun faci la seva
 * pròpia crida al backend quan s'obri la seva pestanya (càrrega mandrosa).
 */
export interface TextAnalyzerProps {
    task_id: string;
    analyzedText: any;                       // dades del mode morfològic (ja obtingudes)
    saveFormattedTextCallback: CallableFunction;
    language?: string;
    plainText?: string;                      // text pla, per als modes de càrrega mandrosa
}

type AnalyzerMode = 'morphology' | 'pragmatics' | 'phrasalVerbs' | 'discourse' | 'semantics' | 'classifier';

interface ModeDef {
    key: AnalyzerMode;
    label: string;                           // clau i18n
    languages?: string[];                    // absent = disponible per a totes
}

const MODES: ModeDef[] = [
    { key: 'morphology',   label: 'Morphology' },
    // Només l'anàlisi anglesa marca pragmàtica i phrasal verbs.
    { key: 'pragmatics',   label: 'Pragmatic Expressions', languages: ['en'] },
    { key: 'phrasalVerbs', label: 'Phrasal verbs',         languages: ['en'] },
    { key: 'discourse',    label: 'Discourse markers' },
    { key: 'semantics',    label: 'Semantics' },
    { key: 'classifier',   label: 'Classification' },
];

export default function TextAnalyzer(props: TextAnalyzerProps) {
    const { t } = useTranslation();
    const [mode, setMode] = useState<AnalyzerMode>('morphology');
    const language = props.language || 'en';

    return (
        <div className="text-analyzer">
            <nav className="text-analyzer-tabs">
                { MODES.filter(m => !m.languages || m.languages.includes(language)).map(m => (
                    <button
                        key={ m.key }
                        type="button"
                        className={ 'analyzer-tab analyzer-tab-' + m.key + (mode === m.key ? ' active' : '') }
                        onClick={ () => setMode(m.key) }>
                        { t(m.label) }
                    </button>
                )) }
            </nav>

            <div className="text-analyzer-panel">
                { (mode === 'morphology' || mode === 'pragmatics' || mode === 'phrasalVerbs') &&
                    <AnalyzedText
                        key={ mode }
                        task_id={ props.task_id }
                        analyzedText={ props.analyzedText }
                        saveFormattedTextCallback={ props.saveFormattedTextCallback }
                        language={ language }
                        section={ mode } />
                }
                { mode === 'discourse' &&
                    <DiscourseMarkersPanel task_id={ props.task_id } plainText={ props.plainText } language={ language } />
                }
                { mode === 'semantics' &&
                    <SemanticsPanel task_id={ props.task_id } plainText={ props.plainText } language={ language } />
                }
                { mode === 'classifier' &&
                    <ClassifierPanel task_id={ props.task_id } plainText={ props.plainText } language={ language } />
                }
            </div>
        </div>
    );
}
